/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ComponentType, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {StateDotIcon} from '../../../icons/current/StateDotIcon';
import {dlsColor, dlsShadow} from '../../../theme/theme';
import {IconProps, IconSize} from '../../../types/types';

export interface AssetSummaryCardStatusProps {
  stateLabel: string;
  stateTextColor: string;
  stateBackgroundColor: string;
  StateIcon?: ComponentType<IconProps>;
  stateIconColor?: string;
  // renders a State Dot after `StateIcon` in this color - the DLS
  // "Moving (No Ignition)" Tag shows the no-ignition glyph AND a dot ...
  stateDotColor?: string;
  // the State Tag label is uppercased by default (Asset List Card); the Asset
  // Details Drawer header sets this to show the label as translated ...
  keepLabelCase?: boolean;
  dateTime?: string;
  // omitted (not just blank) when the asset is stationary, per ION-7395 -
  // the caller decides stationary-ness and simply doesn't pass a speed ...
  speed?: string;
  // drops the fixed 166px width so the column hugs its content - used by
  // the Asset Details Drawer header on phone, where the fixed width left
  // ~50px of dead space next to the asset name ...
  hugWidth?: boolean;
  // tops the column level with the asset name rather than centring it in the
  // header row - the Asset Details Drawer header, whose row is taller than
  // its content ...
  topAligned?: boolean;
}

const useStyles = makeStyles(() => ({
  // fixed width per ION-7395 ("Right section remains fixed width") and the
  // Figma "Header Right" frame (166px). `alignSelf: center` keeps this
  // column vertically centered within the header row's own height (set by
  // whichever side - this one or the Identity column - is taller) rather
  // than top-anchored, so omitting the Speed row (a stationary asset) isn't
  // left as visible dead space below a now-shorter column ...
  status : {
    display       : 'flex',
    width         : '166px',
    flexShrink    : 0,
    flexDirection : 'column',
    alignItems    : 'flex-end',
    alignSelf     : 'center',
    gap           : '4px',
    whiteSpace    : 'nowrap'
  },
  statusHug : {
    width : 'auto'
  },
  statusTop : {
    alignSelf : 'flex-start'
  },
  tag : {
    display      : 'flex',
    alignItems   : 'center',
    gap          : '4px',
    padding      : '4px',
    borderRadius : '4px',
    boxShadow    : dlsShadow(3)
  },
  tagLabel : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'uppercase'
  },
  tagLabelKeepCase : {
    textTransform : 'none'
  },
  dateTime : {
    fontFamily : 'Inter',
    fontWeight : 600,
    fontSize   : '10px',
    lineHeight : '12px',
    color      : dlsColor('grey2')
  },
  speed : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  }
}));

/**
 * AssetSummaryCardStatus renders the card's Header Right (Asset Status):
 * a colored State Tag, the Date/Time, and the Speed - stacked vertically,
 * right-aligned, never wrapping or truncating. Per ION-7395, the Speed row
 * is entirely omitted (not just left blank) when the asset is stationary -
 * simply don't pass `speed` in that case.
 *
 * State color/label/icon are resolved by the caller (there is no existing
 * DLS "Tag/State" component covering every asset state at implementation
 * time), so this component only renders whatever it's given - `StateIcon`
 * (e.g. a colored dot or the no-ignition glyph) is optional and simply
 * omitted when not supplied.
 *
 * ```typescript
 *
 *   import {AssetSummaryCardStatus} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCardStatus';
 *   import {StateDotIcon} from '@ion/dls/src/icons/current/StateDotIcon';
 *   ...
 *
 *   <AssetSummaryCardStatus stateLabel="Moving"
 *     stateTextColor="#397F17"
 *     stateBackgroundColor="rgba(89, 161, 55, 0.1)"
 *     StateIcon={StateDotIcon}
 *     stateIconColor="#59A137"
 *     dateTime="12/16/2025 - 11:08 AM CST"
 *     speed="11 MPH" />
 *
 * ```
 *
 */
export const AssetSummaryCardStatus = ({
  stateLabel,
  stateTextColor,
  stateBackgroundColor,
  StateIcon,
  stateIconColor,
  stateDotColor,
  keepLabelCase = false,
  dateTime,
  speed,
  hugWidth = false,
  topAligned = false
}: AssetSummaryCardStatusProps) => {
  // the state colors are set inline - prop-driven style values made JSS add
  // a dynamic rule for every mounted card ...
  const classes    = useStyles();
  const tagStyle   = useMemo(() => ({backgroundColor: stateBackgroundColor}), [stateBackgroundColor]);
  const labelStyle = useMemo(() => ({color: stateTextColor}), [stateTextColor]);
  return (
    <div className={classnames(classes.status, {
      [classes.statusHug] : hugWidth,
      [classes.statusTop] : topAligned
    })}>
      <div className={classes.tag} style={tagStyle}>
        {StateIcon && <StateIcon size={IconSize.small} color={stateIconColor || stateTextColor} />}
        {stateDotColor && <StateDotIcon color={stateDotColor} />}
        <span className={classnames(classes.tagLabel, {[classes.tagLabelKeepCase]: keepLabelCase})}
          style={labelStyle}>
          {stateLabel}
        </span>
      </div>
      {dateTime && (
        <span className={classes.dateTime}>
          {dateTime}
        </span>
      )}
      {speed && (
        <span className={classes.speed}>
          {speed}
        </span>
      )}
    </div>
  );
};

export default AssetSummaryCardStatus;
